import { CloseOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { Button } from './button';

export interface LookupOption { id: string; label: string; sub?: string; avatar?: string; badge?: string }

interface Props {
  value: LookupOption | null;
  onChange: (opt: LookupOption | null) => void;
  search: (q: string) => Promise<LookupOption[]>;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
}

export function LookupField({ value, onChange, search, placeholder = 'Search…', id, disabled }: Props) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<LookupOption[]>([]);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const needle = q.trim();
    const mySeq = ++seq.current;
    const timer = window.setTimeout(async () => {
      const r = needle ? await search(needle) : [];
      if (mySeq === seq.current) { setResults(r); setOpen(r.length > 0); }
    }, needle ? 200 : 0);
    return () => window.clearTimeout(timer);
  }, [q, search]);

  if (value) {
    return (
      <div className="lookup-wrap" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="lookup-avatar">{value.avatar ?? value.label.slice(0, 2).toUpperCase()}</span>
        <span style={{ flex: 1 }}>{value.label}{value.badge ? <> <span className="badge badge-green">{value.badge}</span></> : null}</span>
        <Button size="sm" variant="ghost" aria-label="Clear" disabled={disabled} onClick={() => onChange(null)}><CloseOutlined /></Button>
      </div>
    );
  }

  return (
    <div className="lookup-wrap">
      <input
        id={id}
        className="form-control"
        placeholder={placeholder}
        value={q}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
      />
      {open && results.length > 0 ? (
        <div className="lookup-results" role="listbox">
          {results.map((r) => (
            <div key={r.id} className="lookup-item" role="option" aria-selected={false} onMouseDown={() => { onChange(r); setQ(''); setOpen(false); }}>
              <span className="lookup-avatar">{r.avatar ?? r.label.slice(0, 2).toUpperCase()}</span>
              <span style={{ flex: 1 }}>
                <div>{r.label}{r.badge ? <> <span className="badge badge-green">{r.badge}</span></> : null}</div>
                {r.sub ? <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.sub}</div> : null}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
